import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PermissionAction, PermissionService } from '../services/permission.service';

interface RoutePermission {
  prefix: string;
  module: string;
  action: PermissionAction;
}

const ROUTE_PERMISSIONS: RoutePermission[] = [
  { prefix: '/apps/trips/add', module: 'trips', action: 'create' },
  { prefix: '/apps/trips', module: 'trips', action: 'view' },
  { prefix: '/apps/fuel-orders/add', module: 'fuel_orders', action: 'create' },
  { prefix: '/apps/fuel-orders/edit', module: 'fuel_orders', action: 'edit' },
  { prefix: '/apps/fuel-orders/payables', module: 'fuel_orders_payables', action: 'view' },
  { prefix: '/apps/fuel-orders', module: 'fuel_orders', action: 'view' },
  { prefix: '/apps/stations/add', module: 'stations', action: 'create' },
  { prefix: '/apps/stations', module: 'stations', action: 'view' },
  { prefix: '/apps/parking/yards/add', module: 'parking_yards', action: 'create' },
  { prefix: '/apps/parking/yards', module: 'parking_yards', action: 'view' },
  { prefix: '/apps/parking/owners/add', module: 'parking_owners', action: 'create' },
  { prefix: '/apps/parking/owners', module: 'parking_owners', action: 'view' },
  { prefix: '/apps/parking/sessions', module: 'parking_sessions', action: 'view' },
  { prefix: '/apps/parking/bills', module: 'parking_bills', action: 'view' },
  { prefix: '/apps/parking/config', module: 'parking_config', action: 'view' },
  { prefix: '/apps/parking', module: 'parking_sessions', action: 'view' },
  { prefix: '/apps/vehicles/add', module: 'vehicles', action: 'create' },
  { prefix: '/apps/vehicles', module: 'vehicles', action: 'view' },
  { prefix: '/apps/trailers/add', module: 'trailers', action: 'create' },
  { prefix: '/apps/trailers', module: 'trailers', action: 'view' },
  { prefix: '/apps/drivers', module: 'drivers', action: 'view' },
  { prefix: '/apps/inventory/tyres', module: 'inventory_tyres', action: 'view' },
  { prefix: '/apps/inventory', module: 'inventory', action: 'view' },
  { prefix: '/apps/payroll/add', module: 'payroll', action: 'create' },
  { prefix: '/apps/payroll/statutory', module: 'payroll_statutory', action: 'view' },
  { prefix: '/apps/payroll/deduction-types', module: 'payroll_deduction_types', action: 'view' },
  { prefix: '/apps/payroll/penalties/add', module: 'payroll_penalties', action: 'create' },
  { prefix: '/apps/payroll/penalties', module: 'payroll_penalties', action: 'view' },
  { prefix: '/apps/payroll/reports/department-analysis', module: 'payroll_department', action: 'view' },
  { prefix: '/apps/payroll/reports/housing-levy', module: 'payroll_housing', action: 'view' },
  { prefix: '/apps/payroll/reports/shif', module: 'payroll_shif', action: 'view' },
  { prefix: '/apps/payroll/reports/paye', module: 'payroll_paye', action: 'view' },
  { prefix: '/apps/payroll/reports/nssf', module: 'payroll_nssf', action: 'view' },
  { prefix: '/apps/payroll/reports/penalties', module: 'payroll_penalties', action: 'view' },
  { prefix: '/apps/payroll/reports/bank-transfer', module: 'payroll_bank_transfer', action: 'view' },
  { prefix: '/apps/payroll/reports', module: 'payroll_reports', action: 'view' },
  { prefix: '/apps/payroll', module: 'payroll', action: 'view' },
  { prefix: '/apps/loans/add', module: 'loans', action: 'create' },
  { prefix: '/apps/loans', module: 'loans', action: 'view' },
  { prefix: '/apps/advances/add', module: 'advances', action: 'create' },
  { prefix: '/apps/advances', module: 'advances', action: 'view' },
  { prefix: '/apps/finance/expenses/add', module: 'finance', action: 'create' },
  { prefix: '/apps/finance/expenses/categories', module: 'finance_expense_categories', action: 'view' },
  { prefix: '/apps/finance/expenses', module: 'finance', action: 'view' },
  { prefix: '/apps/finance/petty-cash/add', module: 'finance_petty_cash', action: 'create' },
  { prefix: '/apps/finance/petty-cash', module: 'finance_petty_cash', action: 'view' },
  { prefix: '/apps/finance', module: 'finance', action: 'view' },
  { prefix: '/apps/payments/methods/add', module: 'payments_methods', action: 'create' },
  { prefix: '/apps/payments/methods/edit', module: 'payments_methods', action: 'edit' },
  { prefix: '/apps/payments/methods', module: 'payments_methods', action: 'view' },
  { prefix: '/apps/payments', module: 'payments', action: 'view' },
  { prefix: '/apps/reports/station-payables', module: 'fuel_orders_payables', action: 'view' },
  { prefix: '/apps/reports/station-credit', module: 'reports_station_credit', action: 'view' },
  { prefix: '/apps/reports/station-orders', module: 'reports_station_orders', action: 'view' },
  { prefix: '/apps/reports/station-fuel-account', module: 'reports_station_fuel', action: 'view' },
  { prefix: '/apps/reports/fuel-orders', module: 'reports_fuel_orders', action: 'view' },
  { prefix: '/apps/reports/vehicle-expenses', module: 'reports_vehicle_expenses', action: 'view' },
  { prefix: '/apps/reports/expenses', module: 'reports_expenses', action: 'view' },
  { prefix: '/apps/reports/petty-cash', module: 'reports_petty_cash', action: 'view' },
  { prefix: '/apps/reports/workshop-stock', module: 'reports_inventory', action: 'view' },
  { prefix: '/apps/reports/tyres', module: 'reports_tyres', action: 'view' },
  { prefix: '/apps/reports', module: 'reports', action: 'view' },
  { prefix: '/apps/users/add', module: 'users', action: 'create' },
  { prefix: '/apps/users/edit', module: 'users', action: 'edit' },
  { prefix: '/apps/users/profile', module: 'users', action: 'view' },
  { prefix: '/apps/users/list', module: 'users', action: 'view' },
  { prefix: '/apps/roles/add', module: 'roles', action: 'create' },
  { prefix: '/apps/roles/permissions', module: 'roles', action: 'edit' },
  { prefix: '/apps/roles/edit', module: 'roles', action: 'edit' },
  { prefix: '/apps/roles', module: 'roles', action: 'view' },
  { prefix: '/apps/branches/add', module: 'branches', action: 'create' },
  { prefix: '/apps/branches', module: 'branches', action: 'view' },
  { prefix: '/apps/hubs/add', module: 'branches', action: 'create' },
  { prefix: '/apps/hubs', module: 'branches', action: 'view' },
  { prefix: '/apps/companies/settings', module: 'company', action: 'view' },
];
ROUTE_PERMISSIONS.sort((a, b) => b.prefix.length - a.prefix.length);

const OPEN_PREFIXES = [
  '/apps/users/account',
  '/authentication',
];

function matchRoute(url: string): RoutePermission | null {
  const path = url.split('?')[0];
  if (OPEN_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null;
  }
  return ROUTE_PERMISSIONS.find((item) => path === item.prefix || path.startsWith(`${item.prefix}/`)) || null;
}

export const permissionGuard: CanActivateFn = (_route, state) => {
  const permissions = inject(PermissionService);
  permissions.hydrateFromStoredUser();
  const match = matchRoute(state.url);
  if (!match || permissions.can(match.module, match.action)) {
    return true;
  }
  const toastr = inject(ToastrService);
  toastr.error('You do not have permission to access this page.');
  return inject(Router).parseUrl('/dashboard');
};
